import styled from 'styled-components';
import { toRem, baseVariables } from '@marriott/mi-ui-library';
import { chatKeyboardFocusVisible } from '../../styles/chatFocus';

/** Positions chat title menu below the trigger (WEB-192172 #6). */
export const ChatNameControl = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
`;

export const RenameInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--general-spacer-02, ${toRem(8)});
`;

export const RenameInput = styled.input`
  font-family: var(--typography-subtitle-font-family, 'Swiss721_BT:Medium', sans-serif);
  font-size: var(--typography-t-subtitle-s-font-size, 0.875rem) !important; /* 14px consistent with Figma */
  font-weight: var(--typography-subtitle-xl-font-weight, 700); /* Updated to match Figma design */
  border-radius: var(--general-spacer-01, ${toRem(4)});
  flex: 1;
  border: none !important;
  ${chatKeyboardFocusVisible}
  /* Match Text component padding for consistency */
  padding: var(--general-spacer-01, 0.25rem) var(--general-spacer-02, 0.5rem) !important;
  background: var(--color-set-brand-bg-color, #fff);
  color: var(--color-set-brand-fg-color, #1c1c1c);
  line-height: 1.25;
  letter-spacing: 0; /* Added letter-spacing to match Figma design */

  &:focus {
    border: none !important;
    font-size: ${toRem(14)} !important; /* 14px consistent with Figma */
  }

  /* Mobile specific styles */
  @media (max-width: 992px) {
    max-width: 140px;
    font-size: ${toRem(16)} !important; /* Prevent iOS zoom on input focus */
    /* Match mobile Text component padding */
    padding: var(--general-spacer-01, 0.25rem) var(--general-spacer-01, 0.25rem) !important;

    &:focus {
      font-size: ${toRem(16)} !important;
    }
  }

  /* Desktop padding to match Text component */
  @media ${baseVariables.mediaQuery.lg} {
    padding: var(--general-spacer-02, 0.5rem) var(--general-spacer-03, 0.75rem) !important;
  }
`;

export const Text = styled.span`
  display: block;
  font-family: var(--typography-subtitle-font-family, 'Swiss721_BT:Medium', sans-serif);
  font-size: var(--typography-t-subtitle-s-font-size, 0.875rem); /* 14px for mobile following Figma */
  font-weight: var(--typography-subtitle-xl-font-weight, 700); /* Updated to match Figma design */
  line-height: 1.25;
  letter-spacing: 0; /* Added letter-spacing to match Figma design */
  color: var(--color-set-brand-fg-color, #1c1c1c);
  /* Match Figma text section padding */
  padding: var(--general-spacer-01, 0.25rem) var(--general-spacer-02, 0.5rem);

  /* Mobile text styling - matches Figma mobile design */
  @media (max-width: 992px) {
    max-width: 176px; /* Match Figma mobile design width */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--typography-t-subtitle-s-font-size, 0.875rem);
    /* Adjusted mobile padding to match Figma spacing */
    padding: var(--general-spacer-01, 0.25rem) var(--general-spacer-01, 0.25rem);
  }

  /* Desktop text styling - matches Figma desktop design */
  @media ${baseVariables.mediaQuery.lg} {
    font-size: var(--typography-t-subtitle-m-font-size, 1rem); /* 16px for desktop */
    max-width: none;
    /* Desktop padding to match Figma specifications */
    padding: var(--general-spacer-02, 0.5rem) var(--general-spacer-03, 0.75rem);
  }
`;

// Menu button - visible on mobile per Figma design, hidden on desktop
export const MenuButton = styled.button`
  /* Mobile default - show menu button following Figma mobile design */
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--color-set-brand-fg-color, #1c1c1c);
  width: 32px;
  height: 32px;
  border-radius: var(--general-spacer-02, ${toRem(8)}); /* 8px border radius from Figma */
  flex-shrink: 0;
  transition: background-color 0.2s ease;
  min-width: 32px;
  min-height: 32px;
  ${chatKeyboardFocusVisible}

  /* Mobile - show menu button per Figma mobile design */
  @media (max-width: 992px) {
    display: flex;
  }

  /* Desktop styling - hide on desktop per typical header behavior */
  @media ${baseVariables.mediaQuery.lg} {
    display: none;
  }

  &:hover {
    background-color: var(--colors-neutral-10, #f4f4f4);
  }

  &:active {
    background-color: var(--color-set-horizontal-vertical-rule, #c4c4c4);
  }

  /* Icon styling to match Figma - 24px icon within 32px button */
  .icon-menu {
    font-size: 1.5rem; /* 24px to match Figma design */
    line-height: 1;
  }
`;
